import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  Card,
  CircularProgress,
  ContainerColumnLeft,
  ContainerColumnsWrapper,
  LinearProgress,
  Typography,
} from '@occc-app/design-system';
import { isAudienceActiveButNotReady } from '@occc-app/routes/Audience/utils';

import { useConfig } from '@occc/config';
import { AudienceSizeOverTime } from './AudienceSizeOverTime';
import { AudienceTotalSize } from './AudienceTotalSize';

import { Purchases } from '../components/Purchases/Purchases';
import { AgeWithin } from '../components/AgeWithin/AgeWithin';
import { MemberAttributes } from '../components/FuelAndConvenienceProfile/MemberAttributes';
import { Segments } from '../components/Segments/Segments';
import { Audience } from '../types';
import { errorMessage, noDataMessage } from '../copies';
import { Fulfillments } from '../components/Fulfillment/Fulfillments';
import { FixedDateRangeAudienceWarning } from '../components/FixedDateRangeAudienceWarning';
import { WalletSteeringItems } from '../components/WalletSteering/WalletSteeringItems';
import { QualifiedEventsDetails } from '../components/QualifiedEvents/QualifiedEventsDetails';
import { SurveysDetails } from '../components/Surveys/SurveysDetails';
import { CombinedAudiencesDetails } from '../components/CombinedAudiences/CombinedAudiencesDetails';
import { ExternalIdentityVerificationDetails } from '../components/ExternalIdentityVerification/external-identity-verification-details.component';

type AudienceDetailsProps = {
  loading: boolean;
  error?: ApolloError;
  audience?: Audience;
};

export const AudienceDetails = ({ audience, loading, error }: AudienceDetailsProps) => {
  const { config } = useConfig();

  const isWarningVisible = useMemo(
    () =>
      !!audience &&
      isAudienceActiveButNotReady(
        audience.status,
        audience.hasBeenBackFilled,
        !!audience.targetingAttributes.purchases.find((item) => item.startDate && item.endDate),
      ),
    [audience],
  );

  if (error) {
    return <Typography color='error'>{errorMessage}</Typography>;
  }

  if (loading && !audience) {
    return <CircularProgress />;
  }

  if (!audience) {
    return <Typography color='error'>{noDataMessage}</Typography>;
  }

  const {
    ageWithin,
    fuelAndConvenienceProfile,
    purchases,
    segments,
    fulfillments,
    homeStore,
    walletSteeringItems,
    productInterests,
    qualifiedEvents,
    lastVisit,
    pointBalance,
    membershipCreated,
    employeeTargetingAttributes,
    surveyItems,
    combinedAudiences,
    activeSubBrandUuids,
    externalIdentityVerification,
    communicationPreference,
  } = audience.targetingAttributes;

  const hasHomeStoreLocations = (homeStore?.locations?.length ?? 0) > 0;
  const hasHomeStoreLocationGroups = (homeStore?.locationGroups?.length ?? 0) > 0;
  const communicationPreferenceCriteria = communicationPreference?.[0]?.criteria ?? [];

  const isMemberAttributesSectionVisible = Boolean(
    fuelAndConvenienceProfile.length ||
      productInterests ||
      lastVisit ||
      pointBalance ||
      membershipCreated ||
      employeeTargetingAttributes ||
      hasHomeStoreLocations ||
      hasHomeStoreLocationGroups ||
      activeSubBrandUuids?.length ||
      communicationPreferenceCriteria.length,
  );

  return (
    <ContainerColumnsWrapper>
      <ContainerColumnLeft>
        {isWarningVisible && (
          <Box mb={2}>
            <Card>
              <FixedDateRangeAudienceWarning />
            </Card>
          </Box>
        )}
        <Card>
          {loading && <LinearProgress />}
          <AudienceSizeOverTime audienceId={audience.uuid} />
        </Card>
      </ContainerColumnLeft>

      <AudienceTotalSize
        audienceMembershipCount={audience.membershipCount}
        audienceUuid={audience.uuid}
        status={audience.status}
        updatedAt={audience.updatedAt}
        draftCount={audience.draftCount}
        draftCountCalculationRequestedAt={audience.draftCountCalculationRequestedAt}
        lastDraftCountCalculationAt={audience.lastDraftCountCalculationAt}
      />

      {ageWithin && <AgeWithin ageWithin={ageWithin} />}
      {isMemberAttributesSectionVisible && (
        <MemberAttributes
          fuelAndConvenienceProfile={fuelAndConvenienceProfile}
          productInterests={productInterests}
          lastVisit={lastVisit}
          pointBalance={pointBalance}
          membershipCreated={membershipCreated}
          employeeStatus={employeeTargetingAttributes}
          homeStore={homeStore}
          activeSubBrandUuid={activeSubBrandUuids?.[0]}
          communicationPreferenceCriteria={communicationPreferenceCriteria}
        />
      )}
      {surveyItems.length > 0 && <SurveysDetails items={surveyItems} />}
      {purchases.length ? <Purchases purchases={purchases} /> : null}
      {qualifiedEvents.length > 0 && (
        <QualifiedEventsDetails attributes={qualifiedEvents} createdAt={audience.createdAt} />
      )}
      {segments.length ? <Segments segments={segments} /> : null}
      {fulfillments.length > 0 && <Fulfillments fulfillments={fulfillments} />}
      {config.features.walletSteeringEnabled && walletSteeringItems.length > 0 && (
        <WalletSteeringItems walletSteeringItems={walletSteeringItems} />
      )}
      {combinedAudiences && <CombinedAudiencesDetails combinedAudiences={combinedAudiences} />}
      {!!externalIdentityVerification?.length && (
        <ExternalIdentityVerificationDetails items={externalIdentityVerification} />
      )}
    </ContainerColumnsWrapper>
  );
};
